import React, { forwardRef, useContext, useMemo } from 'react';
import { BreakpointsContext } from '@src/providers/Breakpoints';
import { Link } from '../Link';
import { ITextProps } from './models';
import { getClass, getClasses } from '@src/utils/getClass';
import { classnames } from '@src/utils/classnames';
import { mapToUpFirstSymbol } from './utils';
import styled from './Text.module.scss';
import { useExperiment } from '@src/hooks/useExperiment';
import { getColorClass } from '@src/utils/colorUtils';

/** Компонент для вывода текста */
export const Text = forwardRef<HTMLElement, ITextProps>((props, ref) => {
  const {
    children: propsChildren,
    textDecoration = '',
    className,
    size = 'primary',
    uppercase,
    weight = 'regular',
    color = '',
    nowrap,
    align = 'left',
    id,
    tag: propsTag,
    href,
    target,
    withMargin,
    disableNextJs,
    noOfLines = '',
    ...rest
  } = useExperiment<ITextProps>(props);

  const { isMobile } = useContext(BreakpointsContext);
  const { breakpoint } = useContext(BreakpointsContext);

  // https://www.figma.com/file/7tSylW4PyQ3ZacyQYja5uK/Design-System?node-id=929%3A31588
  const sizeClasses = isMobile ? getClass(styled, 'mobileSize', size) : getClass(styled, 'size', size);
  const marginClasses = withMargin
    ? isMobile
      ? getClass(styled, 'marginBottonMobile', size)
      : getClass(styled, 'marginBotton', size)
    : undefined;

  const classes = classnames(
    className,
    styled.root,
    sizeClasses,
    marginClasses,
    ...getClasses(styled, { align, weight, textDecoration }),
    {
      [styled.uppercase]: typeof uppercase === 'boolean' && uppercase,
      [styled.nowrap]: nowrap,
    },
    ...getClasses(styled, { noOfLines }, breakpoint),
    getColorClass({ view: color, property: 'color' }),
  );

  let tag = 'p';

  const children = useMemo(
    () => (uppercase === 'first' ? mapToUpFirstSymbol(propsChildren) : propsChildren),
    [propsChildren, uppercase],
  );

  switch (size) {
    case 'h1':
      tag = 'h1';
      break;
    case 'h2':
      tag = 'h2';
      break;
    case 'h3':
      tag = 'h3';
      break;
    case 'h4':
      tag = 'h4';
      break;
    case 'h5':
      tag = 'h5';
      break;
    case 'wysiwyg':
      // Вывод данных из редактора
      // Проблематика в том, что там нет классов у заголовков и абзацев
      tag = 'div';
      break;
  }

  const element = React.createElement(propsTag || tag, { ref, className: classes, ...rest, ['data-id']: id }, children);

  return (
    <Link.Wrapper id={id} href={href} target={target} disableNextJs={disableNextJs}>
      {element}
    </Link.Wrapper>
  );
});

Text.displayName = 'Text';
